import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Space,
  Typography,
} from 'antd';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import CommentAttachmentInput from '../../../components/CommentAttachments/CommentAttachmentInput';
import '../EncyclopediaForm.css';

export default function EncyclopediaEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`/encyclopedia/${id}`)
      .then((res) => {
        if (!cancelled) setEntry(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err.response?.data?.error || 'Failed to load entry';
          dispatch(setMessage({ message: msg, type: 'error' }));
          navigate('/encyclopedia');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, navigate, dispatch]);

  useEffect(() => {
    if (!entry) return;
    form.setFieldsValue({
      name: entry.name ?? '',
      description: entry.description ?? '',
      attachments: Array.isArray(entry.attachments) ? entry.attachments : [],
    });
  }, [entry, form]);

  const handleSubmit = async (values) => {
    if (!values.name?.trim()) {
      dispatch(setMessage({ message: 'Name is required', type: 'warning' }));
      return;
    }
    const list = Array.isArray(values.attachments) ? values.attachments : [];
    if (list.length > 5) {
      dispatch(setMessage({ message: 'Maximum 5 attachments allowed', type: 'warning' }));
      return;
    }
    setSubmitLoading(true);
    try {
      await axios.put(`/encyclopedia/${id}`, {
        name: values.name.trim(),
        description: values.description != null ? String(values.description).trim() : '',
        attachments: list,
      });
      dispatch(setMessage({ message: 'Entry updated', type: 'success' }));
      navigate(`/encyclopedia/${id}`);
    } catch (err) {
      const msg = err.response?.data?.error || 'Update failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="encyclopedia-form-page">
        <div className="encyclopedia-form-loading">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!entry) return null;

  const { Title } = Typography;

  return (
    <div className="encyclopedia-form-page">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div className="encyclopedia-form-header">
          <div className="encyclopedia-form-header-left">
            <Title level={3} style={{ margin: 0 }}>
              Edit encyclopedia entry
            </Title>
            {entry.name && <p className="encyclopedia-form-subtitle">{entry.name}</p>}
          </div>
          <Space>
            <Button
              type="primary"
              size="large"
              loading={submitLoading}
              onClick={() => form.submit()}
              className="app-btn-primary"
            >
              Save changes
            </Button>
            <Button size="large" onClick={() => navigate(-1)} className="app-btn-cancel">
              Cancel
            </Button>
          </Space>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit} className="encyclopedia-form">
          <Card className="encyclopedia-form-card">
            <div className="encyclopedia-form-body">
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item
                    name="name"
                    label="Name"
                    required
                    rules={[{ required: true, message: 'Name is required' }]}
                  >
                    <Input placeholder="Name" maxLength={200} />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item name="description" label="Description">
                    <Input.TextArea placeholder="Description" rows={6} maxLength={5000} showCount />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    name="attachments"
                    label="Attachments (up to 5 files)"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (Array.isArray(value) && value.length > 5) {
                            return Promise.reject(new Error('Maximum 5 attachments'));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <CommentAttachmentInput />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Card>
        </Form>
      </Space>
    </div>
  );
}
